/**
 * PageBlock Component
 * 
 * A single draggable digital page block in the content editor.
 * Supports:
 * - Rich text editing with formatting (bold, italic, indent)
 * - Drag handle for reordering
 * - Delete/merge actions
 * - Pending extraction preview
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Trash2,
  ChevronUp,
  ChevronDown,
  Check,
  X,
} from 'lucide-react';
import { glassStyles } from '@/lib/design';
import type { DigitalPageBlock, FormattedLine, PendingExtraction } from './types';

// ============================================================================
// Types
// ============================================================================

interface PageBlockProps {
  block: DigitalPageBlock;
  isSelected: boolean;
  pendingExtraction: PendingExtraction | null;
  onSelect: () => void;
  onChange: (lines: FormattedLine[]) => void;
  onDelete: () => void;
  onSplitAtCursor: (lineIndex: number, offsetInLine?: number) => void;
  onAcceptExtraction: () => void;
  onRejectExtraction: () => void;
  onMoveExtraction: (direction: 'up' | 'down') => void;
}

// ============================================================================
// Component
// ============================================================================

export function PageBlock({
  block,
  isSelected,
  pendingExtraction,
  onSelect,
  onChange,
  onDelete,
  onSplitAtCursor,
  onAcceptExtraction,
  onRejectExtraction,
  onMoveExtraction,
}: PageBlockProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Drag-and-drop setup
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [block.lines]);

  // Convert lines to editable text (simplified - just newlines)
  const blockText = block.lines.map(line => {
    const indent = '  '.repeat(line.indent);
    return indent + line.text;
  }).join('\n');

  // Handle text changes
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    const newLines: FormattedLine[] = newText.split('\n').map(line => {
      // Detect indent level by counting leading spaces
      const match = line.match(/^(\s*)/);
      const leadingSpaces = match ? match[1].length : 0;
      const indent = Math.floor(leadingSpaces / 2);
      // Use trimStart() to preserve trailing spaces (allows typing with spaces)
      const text = line.trimStart();
      
      return {
        text,
        indent: Math.min(indent, 3), // Max 3 indent levels
      };
    });
    onChange(newLines);
  }, [onChange]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    
    // Cmd/Ctrl + Enter = Split block at cursor (within-line supported)
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      const cursorPosition = textarea.selectionStart;
      const textBeforeCursor = textarea.value.slice(0, cursorPosition);
      const lineIndex = Math.max(0, textBeforeCursor.split('\n').length - 1);
      const lineStart = textBeforeCursor.lastIndexOf('\n') + 1;
      const offsetInLine = cursorPosition - lineStart;
      onSplitAtCursor(lineIndex, offsetInLine);
      return;
    }
    
    // Tab = Increase indent
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;
      
      // Find start of current line
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      
      // Insert two spaces at line start
      const newValue = value.slice(0, lineStart) + '  ' + value.slice(lineStart);
      
      // Update through onChange
      const newLines: FormattedLine[] = newValue.split('\n').map(line => {
        const match = line.match(/^(\s*)/);
        const leadingSpaces = match ? match[1].length : 0;
        return {
          text: line.trim(),
          indent: Math.min(Math.floor(leadingSpaces / 2), 3),
        };
      });
      onChange(newLines);
      
      // Restore cursor position
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
      return;
    }
    
    // Shift+Tab = Decrease indent
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      const start = textarea.selectionStart;
      const value = textarea.value;
      
      // Find start of current line
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      
      // Remove up to two leading spaces
      let newValue = value;
      if (value.slice(lineStart, lineStart + 2) === '  ') {
        newValue = value.slice(0, lineStart) + value.slice(lineStart + 2);
      } else if (value.slice(lineStart, lineStart + 1) === ' ') {
        newValue = value.slice(0, lineStart) + value.slice(lineStart + 1);
      }
      
      if (newValue !== value) {
        const newLines: FormattedLine[] = newValue.split('\n').map(line => {
          const match = line.match(/^(\s*)/);
          const leadingSpaces = match ? match[1].length : 0;
          return {
            text: line.trim(),
            indent: Math.min(Math.floor(leadingSpaces / 2), 3),
          };
        });
        onChange(newLines);
        
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = Math.max(lineStart, start - 2);
        }, 0);
      }
      return;
    }
  }, [onChange, onSplitAtCursor]);

  // Render pending extraction inline
  const renderPendingExtraction = () => {
    if (!pendingExtraction) return null;
    
    const { status, extractedText, inferredInsertIndex, error } = pendingExtraction;
    
    if (status === 'extracting') {
      return (
        <div className="my-2 p-3 rounded-lg bg-[#5E5CE6]/20 border border-[#5E5CE6]/40 animate-pulse">
          <div className="flex items-center gap-2 text-[#5E5CE6] text-sm">
            <div className="w-4 h-4 border-2 border-[#5E5CE6] border-t-transparent rounded-full animate-spin" />
            Extracting text from selected region...
          </div>
        </div>
      );
    }
    
    if (status === 'error') {
      return (
        <div className="my-2 p-3 rounded-lg bg-[#FF453A]/20 border border-[#FF453A]/40">
          <div className="text-[#FF453A] text-sm">{error || 'Failed to extract text'}</div>
          <button
            onClick={onRejectExtraction}
            className="mt-2 text-xs text-white/60 hover:text-white"
          >
            Dismiss
          </button>
        </div>
      );
    }
    
    if (status === 'ready' && extractedText) {
      return (
        <div className="my-2 p-3 rounded-lg bg-[#30D158]/20 border border-[#30D158]/40">
          <div className="text-sm text-white/90 mb-3 whitespace-pre-wrap font-mono">
            "{extractedText}"
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onAcceptExtraction}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#30D158] text-white text-sm font-medium hover:brightness-110 transition-all"
            >
              <Check className="w-4 h-4" />
              Accept
            </button>
            <button
              onClick={() => onMoveExtraction('up')}
              className="p-1.5 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 transition-all"
              title="Move up"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => onMoveExtraction('down')}
              className="p-1.5 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 transition-all"
              title="Move down"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
            <button
              onClick={onRejectExtraction}
              className="p-1.5 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 transition-all"
              title="Discard"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-xl transition-all ${
        isSelected 
          ? 'ring-2 ring-[#0A84FF] bg-[#0A84FF]/10' 
          : 'bg-white/5 hover:bg-white/8'
      } ${isDragging ? 'shadow-2xl' : ''}`}
      onClick={onSelect}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <div className="flex items-center gap-2">
          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            className="p-1 rounded cursor-grab hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          
          <span className="text-sm font-medium text-white/70">
            Page {block.pageNumber}
          </span>
          
          <span className="text-xs text-white/30">
            {block.lines.reduce((acc, l) => acc + l.text.length, 0)} chars
          </span>
        </div>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-1.5 rounded-lg hover:bg-[#FF453A]/20 text-white/40 hover:text-[#FF453A] transition-all"
          title="Delete block"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      
      {/* Content */}
      <div className="p-3">
        {/* Pending extraction at inferred position */}
        {pendingExtraction && pendingExtraction.inferredInsertIndex === 0 && renderPendingExtraction()}
        
        <textarea
          ref={textareaRef}
          value={blockText}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          className="w-full bg-transparent text-white text-base leading-relaxed resize-none focus:outline-none font-sans"
          placeholder="Enter page text..."
          rows={3}
        />
        
        {/* Pending extraction at end */}
        {pendingExtraction && pendingExtraction.inferredInsertIndex > 0 && renderPendingExtraction()}
      </div>
      
      {/* Footer hint */}
      {isSelected && (
        <div className="px-3 py-2 border-t border-white/5 text-xs text-white/30">
          <span className="mr-4">Tab: indent</span>
          <span className="mr-4">Shift+Tab: outdent</span>
          <span>⌘+Enter: split page</span>
        </div>
      )}
    </div>
  );
}

export default PageBlock;
